import React, { useState, useCallback } from "react";
import Loader from "../../Components/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { closeDrawer } from "../../actions/drawerActions";
import { portfolioAdded } from "../../actions/portfolioActions";
import { AddClientPortfolio } from "../../Apis/AddClientPortfolio";
import { handleApiError, showNotification } from "../../actions/notificationActions";
import Toaster from "../../Components/Toaster/Toaster";
import TagManager from "react-gtm-module";
import AutoCompleteTicker from "../../Components/AutoCompleteTicker/AutoCompleteTicker";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OptionExpirationDatePicker from "../OptionExpirationDatePicker/OptionExpirationDatePicker";
import { fetchOptionsChainTickers } from "../../actions/optionChainTickersAction";
import "../AddPortfolioDrawer/AddPortfolioDrawer.scss";

function AddPortfolioDrawer() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [portfolioName, setPortfolioName] = useState("");
  
  const [lineItems, setLineItems] = useState(() => [{ 
    id: `initial_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    ticker_type: "stock",
    symbol: "", 
    quantity: "",
    contractType: "",
    strikePrice: "",
    expirationDate: "",
    optionTicker: ""
  }]);
  const drawerData = useSelector((state) => state.drawer.data);
  const [PliError, setPliError] = useState([{ 
    symbol: "", 
    quantity: "",
    contractType: "",
    strikePrice: "",
    expirationDate: ""
  }]);
  const [portfolioNameError, setPortfolioNameError] = useState("");
  const allAvailableTickers = useSelector(
    (state) => state.availableTickers.availableTickers
  );
  const allAvailableOptionTickers = useSelector(
    (state) => state.availableOptionsTickers.availableOptionsTickers
  );
  const optionChainTickers = useSelector(state => state.optionChainTickers);
  const subscriptionsDetails = useSelector(state => state.userSubscription.subscriptionData) || {};
  const { user_type: userType, customer_id: CustomerID } = subscriptionsDetails;

  const getLineItemOptionData = useCallback((lineItemIndex) => {
    const lineItem = lineItems[lineItemIndex];
    if (!lineItem || !lineItem.optionTicker) return [];
    return optionChainTickers.optionsChainTickers[lineItem.optionTicker] || [];
  }, [lineItems, optionChainTickers.optionsChainTickers]);

  const getAvailableContractTypes = useCallback((lineItemIndex) => {
    const optionData = getLineItemOptionData(lineItemIndex);
    if (!optionData.length) return [];
    const contractTypes = [...new Set(optionData.map(option => option.call_put))];
    return contractTypes.sort();
  }, [getLineItemOptionData]);

  const getAvailableStrikePrices = useCallback((lineItemIndex) => {
    const lineItem = lineItems[lineItemIndex];
    const optionData = getLineItemOptionData(lineItemIndex);
    if (!optionData.length || !lineItem?.contractType || !lineItem?.expirationDate) return [];
    const strikePrices = optionData
      .filter(option => option.call_put === lineItem.contractType &&
        option.expiration_date === lineItem.expirationDate
      )
      .map(option => option.strike_price)
      .filter((price, index, arr) => arr.indexOf(price) === index)
      .sort((a, b) => parseFloat(a) - parseFloat(b));
    return strikePrices;
  }, [getLineItemOptionData, lineItems]);

  const getAvailableExpirationDates = useCallback((lineItemIndex) => {
    const lineItem = lineItems[lineItemIndex];
    const optionData = getLineItemOptionData(lineItemIndex);
    if (!optionData.length || !lineItem?.contractType) return [];
    const expirationDates = optionData
      .filter(option => 
        option.call_put === lineItem.contractType
      )
      .map(option => option.expiration_date)
      .filter((date, index, arr) => arr.indexOf(date) === index)
      .sort();
    return expirationDates;
  }, [getLineItemOptionData, lineItems]);

  const isQuantityFieldEnabled = useCallback((lineItemIndex) => {
    const lineItem = lineItems[lineItemIndex];
    const errors = PliError[lineItemIndex];
    if (!lineItem || !errors) return false;
    if (lineItem.ticker_type === 'option') {
      const hasAllFields = lineItem.optionTicker && lineItem.contractType && lineItem.strikePrice && lineItem.expirationDate;
      const hasNoErrors = !errors.symbol && !errors.contractType && !errors.strikePrice && !errors.expirationDate;
      return hasAllFields && hasNoErrors;
    } else {
      const hasSymbol = lineItem.symbol;
      const hasNoSymbolError = !errors.symbol;
      return hasSymbol && hasNoSymbolError;
    }
  }, [lineItems, PliError]);


  const handleLineItemChange = useCallback((value, index, field) => {
    const newLineItems = [...lineItems];
    const lineItem = newLineItems[index];
    
    if (field === "ticker_type") {
      lineItem.ticker_type = value;
      lineItem.symbol = "";
      lineItem.quantity = "";
      lineItem.optionTicker = "";
      lineItem.contractType = "";
      lineItem.strikePrice = "";
      lineItem.expirationDate = "";
      
      const newErrors = [...PliError];
      newErrors[index] = {
        symbol: "",
        quantity: "",
        contractType: "",
        strikePrice: "",
        expirationDate: "",
      };
      setPliError(newErrors);
    } else if (field === "symbol") {
      lineItem.symbol = value.toUpperCase();
    } else if (field === "quantity") {
      lineItem.quantity = value;
    } else if (field === "optionTicker") {
      const tickerSymbol = value ? value.toUpperCase() : "";
      lineItem.optionTicker = tickerSymbol;
      lineItem.contractType = "";
      lineItem.strikePrice = "";
      lineItem.expirationDate = "";
      lineItem.symbol = "";
      lineItem.quantity = "";
      
      if (tickerSymbol && allAvailableOptionTickers.find(t => t.symbol === tickerSymbol)) {
        if (!optionChainTickers.optionsChainTickers[tickerSymbol]) {
          dispatch(fetchOptionsChainTickers(tickerSymbol));
        }
      }
    } else if (field === "contractType") {
      lineItem.contractType = value;
      lineItem.expirationDate = "";
      lineItem.strikePrice = "";
      lineItem.symbol = "";
      lineItem.quantity = "";
    } else if (field === "expirationDate") {
      lineItem.expirationDate = value;
      lineItem.strikePrice = "";
      lineItem.symbol = "";
      lineItem.quantity = "";
    } else if (field === "strikePrice") {
      lineItem.strikePrice = value;
      lineItem.quantity = "";
    }
      
    if (lineItem.ticker_type === 'option' && lineItem.optionTicker && lineItem.contractType && lineItem.strikePrice && lineItem.expirationDate) {
      const optionData = optionChainTickers.optionsChainTickers[lineItem.optionTicker] || [];
      const matchingOption = optionData.find(option => {
        return option.call_put === lineItem.contractType &&
          option.strike_price === parseFloat(lineItem.strikePrice) &&
          option.expiration_date === lineItem.expirationDate;
      });
      if (matchingOption && matchingOption.opt_sec_id) {
        lineItem.symbol = matchingOption.opt_sec_id;
      }
    }
    setLineItems(newLineItems);
  }, [lineItems, PliError, allAvailableOptionTickers, optionChainTickers, dispatch]);

  const checkTickerRecognition = (tickerData) => {
    const symbolsEntered = tickerData.map((ticker) => ticker.symbol);
    let error = [...PliError];
    tickerData.forEach((tickerObj, index) => {
      const referenceTicker = allAvailableTickers.find(
        (refTicker) => refTicker.symbol === tickerObj.symbol
      );
      if (!referenceTicker) {
        error[index]["symbol"] = "Ticker not recognized";
        error[index]["quantity"] = "";
      } else if (symbolsEntered.indexOf(tickerObj.symbol) !== index) {
        error[index]["symbol"] = "This ticker has already been added";
        error[index]["quantity"] = "";
      } else if (
        tickerObj.symbol.trim() !== "" &&
        tickerObj.quantity.trim() === ""
      ) {
        error[index]["quantity"] = "Please add quantity";
        error[index]["symbol"] = "";
      } else {
        error[index]["symbol"] = "";
        error[index]["quantity"] = "";
      }
    });
    setPliError(error);
  };

  const handleAddLineItem = () => {
    checkTickerRecognition([...lineItems]);
    const countSymbolErrors = () => {
      return PliError.filter(error => error.symbol === '' || (error.symbol && error.symbol !== "Ticker not recognized")).length;
    };
    const limit = subscriptionsDetails.subscription_plan_active_portfolio_line_items_limit;
    if (limit !== 'Infinity' && countSymbolErrors() >= limit) {
      dispatch(showNotification("Add new Portfolio Line Item limit exceeded: Please upgrade your subscription to add more", "info", "toaster"));
      return;
    }
    const uniqueId = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    setLineItems(prevLineItems => [...prevLineItems, { 
      id: uniqueId,
      ticker_type: "stock",
      symbol: "", 
      quantity: "",
      contractType: "",
      strikePrice: "",
      expirationDate: "",
      optionTicker: ""
    }]);
    setPliError(prevPliError => [...prevPliError, { 
      symbol: "", 
      quantity: "",
      contractType: "",
      strikePrice: "",
      expirationDate: ""
    }]);
  };

  const handleDeleteLineItem = (index) => {
    const newLineItems = [...lineItems];
    newLineItems.splice(index, 1);
    setLineItems(newLineItems);
    const newPliError = [...PliError];
    newPliError.splice(index, 1);
    setPliError(newPliError);
  };

  const validateOptionFields = useCallback((lineItemIndex) => {
    const lineItem = lineItems[lineItemIndex];
    const errors = [...PliError];
    let isValid = true;
    if (!lineItem.optionTicker) {
      errors[lineItemIndex].symbol = "Please enter option ticker";
      isValid = false;
    } else {
      errors[lineItemIndex].symbol = "";
    }
    if (!lineItem.contractType) {
      errors[lineItemIndex].contractType = "Please select contract type";
      isValid = false;
    } else {
      errors[lineItemIndex].contractType = "";
    }
    if (!lineItem.strikePrice) {
      errors[lineItemIndex].strikePrice = "Please select strike price";
      isValid = false;
    } else {
      errors[lineItemIndex].strikePrice = "";
    }
    if (!lineItem.expirationDate) {
      errors[lineItemIndex].expirationDate = "Please select expiration date";
      isValid = false;
    } else {
      errors[lineItemIndex].expirationDate = "";
    }
    setPliError(errors);
    return isValid;
  }, [lineItems, PliError]);

  const validatePortfolio = () => {
    let validFlag = true;
    let error = [...PliError];
    let pattern = new RegExp(/^-?[0-9]\d*\.?\d*$/);
    if (!portfolioName) {
      validFlag = false;
      setPortfolioNameError("Please enter a portfolio name");
    }
    if (lineItems.length !== 0) {
      lineItems.forEach((row, index) => {
        if (row.ticker_type === 'option') {
          if (!validateOptionFields(index)) {
            validFlag = false;
          }
        } else {
          if (!row.symbol) {
            error[index]["symbol"] = "Please enter ticker";
            validFlag = false;
          } else {
            const referenceTicker = allAvailableTickers.find(
              (refTicker) => refTicker.symbol === row.symbol
            );
            if (row.symbol && !/^[A-Za-z\-.$^&]+$/.test(row.symbol)) {
              error[index]["symbol"] = "Invalid ticker";
              validFlag = false;
            } else if (!referenceTicker) {
              error[index]["symbol"] = "Ticker not recognized";
              validFlag = true;
            } else if(lineItems.filter(item => item.symbol === row.symbol).length > 1){
              error[index]["symbol"] = "This ticker has already been added";
              validFlag = false;
            } else {
              error[index]["symbol"] = "";
            }
          }
        }
        if (!row.quantity) {
          error[index]["quantity"] = "Please enter quantity";
          validFlag = false;
        } else {
          if (row.quantity && !pattern.test(row.quantity)) {
            error[index]["quantity"] = "Please enter valid quantity";
            validFlag = false;
          } else {
            error[index]["quantity"] = "";
          }
        }
      });
    }
    setPliError(error);
    return validFlag;
  };

  const handleSubmit = async () => {
    if (validatePortfolio()) {
      setIsLoading(true);
      const response = await AddClientPortfolio(
        drawerData.clientUsername,
        portfolioName,
        lineItems.map((item) => ({
          ticker: item.symbol,
          quantity: item.quantity,
        }))
      );
      if (response.status === 200) {
        dispatch(portfolioAdded());
        dispatch(closeDrawer());
        TagManager.dataLayer({
          dataLayer: {
            event: "Add Portfolio",
            userType: userType,
            user_id: CustomerID,
            creation_method: "Manually",
          },
        });
      } else {
        handleApiError(response, dispatch);
      }
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="toast-container">
        <Toaster />
      </div>
      <div className="drawer">
        {isLoading ? (
          <div className="loading-overlay">
            <Loader />
          </div>
        ) : (
          <>
            <div className="drawer-header">
              <button
                className="ai-btn mr16"
                onClick={() => dispatch(closeDrawer())}
              >
                Dashboard
              </button>
              <h1>Add Portfolio</h1>
              <div className="actions"></div>
            </div>
            <div className="drawercontent">
              <div className="portfolioname">
                <p>Portfolio Name</p>
                <input
                  className="ai-form-value"
                  type="text"
                  placeholder="Portfolio Name"
                  value={portfolioName}
                  onChange={(e) => setPortfolioName(e.target.value)}
                />
                <p className="ai-error-message">{portfolioNameError}</p>
              </div>
              <div className="tickercon">
                <div className="tickerlist">
                  {lineItems.map((lineItem, index) => (
                    <div key={`${lineItem.id}-${lineItem.ticker_type}`} className="addticker-wrapper">
                      <div className="title-add-ticker">
                        <div className="switchcontent">
                          Add Stock
                          <div className="toggle-switch-small">
                            <input
                              type="checkbox"
                              id={`ticker-toggle-${index}`}
                              checked={lineItem.ticker_type === 'option'}
                              onChange={(e) => {
                                handleLineItemChange(
                                  e.target.checked ? 'option' : 'stock',
                                  index,
                                  "ticker_type"
                                );
                              }}
                            />
                            <label 
                              htmlFor={`ticker-toggle-${index}`} 
                              className="toggle-label-small"
                            ></label>
                          </div>
                          Add Option
                        </div>
                      </div>
                      {lineItem.ticker_type === 'option' ? (
                        <div className="addOptionticker">
                          <div className="tickerdata">
                            <AutoCompleteTicker
                              key={`option-${lineItem.id}-${lineItem.ticker_type}`}
                              onTickerChange={(event, newValue) => {
                                const tickerSymbol = newValue ? newValue.symbol : "";
                                handleLineItemChange(tickerSymbol, index, "optionTicker");
                              }}
                              toggleOptionTickerFlag={true}
                            />
                            <p className="ai-error-message">
                              {PliError[index]["symbol"]}
                            </p>
                          </div>
                          <div className="tickerdata">
                            <Select
                              value={lineItem.contractType || ""}
                              onChange={(e) => 
                                handleLineItemChange(
                                  e.target.value,
                                  index,
                                  "contractType"
                                )
                              }
                              displayEmpty
                              size="small"
                              disabled={!lineItem.optionTicker || optionChainTickers.isLoadingChain}
                              className="ai-select"
                            >
                              <MenuItem value="" disabled>Contract Type</MenuItem>
                              {getAvailableContractTypes(index).map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                            <p className="ai-error-message">
                              {PliError[index]["contractType"]}
                            </p>
                          </div>
                          <div className="tickerdata">
                            <OptionExpirationDatePicker
                              defaultDate={lineItem.expirationDate || ""}
                              onDateSelect={(date) => 
                                handleLineItemChange(
                                  date,
                                  index,
                                  "expirationDate"
                                )
                              }
                              availableDates={getAvailableExpirationDates(index)}
                              disabled={!lineItem.optionTicker || optionChainTickers.isLoadingChain || !lineItem.contractType}
                              placeholder="Expiration Date"
                            />
                            <p className="ai-error-message">
                              {PliError[index]["expirationDate"]}
                            </p>
                          </div>
                          <div className="tickerdata">
                            <Select
                              value={lineItem.strikePrice || ""}
                              onChange={(e) => 
                                handleLineItemChange(
                                  e.target.value,
                                  index,
                                  "strikePrice"
                                )
                              }
                              displayEmpty
                              size="small"
                              disabled={!lineItem.optionTicker || optionChainTickers.isLoadingChain || !lineItem.contractType || !lineItem.expirationDate}
                              className="ai-select"
                            >
                              <MenuItem value="" disabled>Strike Price</MenuItem>
                              {getAvailableStrikePrices(index).map((price) => (
                                <MenuItem key={price} value={price}>
                                  ${price}
                                </MenuItem>
                              ))}
                            </Select>
                            <p className="ai-error-message">
                              {PliError[index]["strikePrice"]}
                            </p>
                          </div>
                          <div className="tickerdata">
                            <input
                              className="ai-form-value"
                              type="text"
                              placeholder="Quantity"
                              value={lineItem.quantity}
                              onChange={(e) =>
                                handleLineItemChange(
                                  e.target.value,
                                  index,
                                  "quantity"
                                )
                              }
                              disabled={!isQuantityFieldEnabled(index)}
                            />
                            <p className="ai-error-message">
                              {PliError[index]["quantity"]}
                            </p>
                          </div>
                          <button onClick={() => handleDeleteLineItem(index)}>
                            <img src="./Assets/trash.svg" alt="delete" />
                          </button>
                          {optionChainTickers.isLoadingChain && 
                           lineItem.optionTicker && 
                           !optionChainTickers.optionsChainTickers[lineItem.optionTicker] && (
                            <p className="option-loading-message">
                              Fetching Option Contracts...
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="addticker">
                          <div className="tickerdata">
                            <AutoCompleteTicker
                              key={`stock-${lineItem.id}-${lineItem.ticker_type}`}
                              onTickerChange={(event, newValue) => {
                                const tickerSymbol = newValue ? newValue.symbol : "";
                                handleLineItemChange(tickerSymbol, index, "symbol");
                              }}
                              toggleOptionTickerFlag={false}
                            />
                            <p className="ai-error-message">
                              {PliError[index]["symbol"]}
                            </p>
                          </div>
                          <div className="tickerdata">
                            <input
                              className="ai-form-value"
                              type="text"
                              placeholder="Quantity"
                              value={lineItem.quantity}
                              onChange={(e) =>
                                handleLineItemChange(
                                  e.target.value,
                                  index,
                                  "quantity"
                                )
                              }
                              disabled={!isQuantityFieldEnabled(index)}
                            />
                            <p className="ai-error-message">
                              {PliError[index]["quantity"]}
                            </p>
                          </div>
                          <button onClick={() => handleDeleteLineItem(index)}>
                            <img src="./Assets/trash.svg" alt="delete" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <span className="addticker">
                  <button onClick={handleAddLineItem}>+</button>
                </span>
              </div>
              <div className="drawerbottom mt32">
                <button className="ai-btn primary solid" onClick={handleSubmit}>
                  Save
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default AddPortfolioDrawer;
